<template>
  <section class="shared-section">
    <header class="shared-section-header">
      <h2 class="pl-4 shared-section-header-heading sm:pl-0">Contacts</h2>
    </header>

    <!-- Table -->
    <Table
      toolbarHeading="Contact List"
      :withToolbar="true"
      :withSearch="true"
      :withPagination="true"
      :tColumns="$options.tColumns"
      :tData="contacts"
      :fetch="fetchContacts"
    >
      <template #table-toolbar>
        <button
          type="button"
          :disabled="!$canI.create"
          class="shared-table-toolbar-button"
          @click="showFormModal = true"
        >
          <VSvgIcon name="plus" class="h-5 mr-1" /> Add Contact
        </button>
      </template>

      <template #table-data-actions="trData">
        <td class="shared-table-cells-cell">
          <div class="flex justify-center">
            <button
              class="mr-3 shared-table-cells-button round impersonate"
              @click="handleImpersonate(trData.id)"
            >
              <VSvgIcon name="account-arrow-right" class="h-5" />
            </button>
            <button
              :disabled="!$canI.update"
              class="mr-3 shared-table-cells-button round send"
              @click="handleSendInvite(trData.id)"
            >
              <VSvgIcon name="send-email" class="h-5" />
            </button>
            <button
              :disabled="!$canI.update"
              class="mr-3 shared-table-cells-button round edit"
              @click="() => handleSelectEditableContact(trData)"
            >
              <VSvgIcon name="pencil" class="h-5" />
            </button>
            <button
              :disabled="!$canI.delete"
              class="shared-table-cells-button round delete"
              @click="handleDeleteContact(trData.id)"
            >
              <VSvgIcon name="trash" class="h-5" />
            </button>
          </div>
        </td>
      </template>
    </Table>

    <!-- Form Modal -->
    <ModalPlus
      :title="editable ? 'Edit Contact' : 'Add Contact'"
      :buttonText="editable ? 'Save' : 'Add'"
      :show="showFormModal"
      :onClose="handleClose"
      :onProceed="handleSubmit(handleProceed)"
      :loading="formLoading"
      maxWidth="800px"
    >
      <div class="py-8 pb-4">
        <ContactForm
          :errors="errors"
          :initialValues="editableContact"
          :customers="customers"
          :editable="editable"
          @set="handleSetField"
          @filter="handleFilterCustomers"
        />
      </div>
    </ModalPlus>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { format, parseISO } from 'date-fns';

import { safeNullDataset, isPhoneNum, isEmail } from '@/utils';

import { formMixin } from '@/mixins';

import Table from '@/components/shared/table/Table';
import VSvgIcon from '@/components/base/VSvgIcon';
import ModalPlus from '@/components/shared/modal/ModalPlus';
import ContactForm from '@/components/admin/ContactForm';

const DUMMY_PASSWORD = '•••••••••••••••••••';

export default {
  name: 'AdminContacts',
  components: { Table, VSvgIcon, ModalPlus, ContactForm },
  mixins: [
    formMixin(
      {
        subsidary_id: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        internal_id: '',
        password: '',
        customer: null,
      },
      ['internal_id'],
      ['customer'],
      [
        {
          field: 'phone',
          msg: 'Invalid phone number.',
          predicateFn: phone => !isPhoneNum(phone),
        },
        {
          field: 'email',
          msg: 'Invalid email address.',
          predicateFn: email => !isEmail(email),
        },
      ],
    ),
  ],
  data() {
    return {
      showFormModal: false,
      showPreviewModal: false,
      editableContact: {},
      customers: [],
    };
  },
  computed: mapState({
    contacts: state => ({
      data: safeNullDataset(state)(['admin', 'contacts', 'data']).map(c => ({
        subsidary: c.customer.subsidiary_id == '3' ? 'EU' : 'US',
        'first name': c.first_name || '-',
        'last name': c.last_name || '-',
        'email address': c.email || '-',
        'phone #': c.phone || '-',
        'business name': c.customer.company_name || '-',
        'invite sent at':
          format(
            c.invite_sent_at ? parseISO(c.invite_sent_at) : new Date(),
            'MM/dd/yyyy h:mm aaaa',
          ) || '-',
        'completed pretraining': parseInt(c.unlocked_trainings) ? 'Yes' : 'No',
        ...c,
      })),
      meta: state.admin.contacts.meta,
    }),
    templates: state => state.admin.templates.data,
    editable() {
      return this.editableContact && !!Object.keys(this.editableContact).length;
    },
  }),
  watch: {
    editable(isEditable) {
      // We need to make some fields in the formMixing optional/required
      // in order to trigger the right condition in Workato
      this.ignoredFields = isEditable
        ? this.ignoredFields.filter(f => f !== 'internal_id')
        : ['internal_id'];
    },
  },
  methods: {
    async fetchContacts(params) {
      return await this.$store.dispatch('admin/getContacts', params);
    },
    async handleFilterCustomers(text) {
      const params = { search: text };
      const { data } = await this.$http.get('admin/customers/find', { params });
      this.customers = data;
    },
    async handleImpersonate(id) {
      await this.$store.dispatch('auth/impersonate', id);
      const route = this.$router.resolve({ name: '/' });
      window.open(route.href, '_blank');
    },
    async handleSendInvite(id) {
      try {
        await this.$http.post(`/admin/customer_users/${id}/reinvite`);
        this.$store.commit('admin/updateContact', { id, invite_sent_at: new Date() });
      } catch (error) {
        console.error({ ...error });
      }
    },
    handleDeleteContact(id) {
      this.$store.dispatch('admin/deleteContact', id);
    },
    handleSelectEditableContact(contact) {
      this.editableContact = { ...contact, password: DUMMY_PASSWORD }; // add dummy password visual on update
      this.showFormModal = true;
    },
    handleClose() {
      this.handleResetFields();
      this.handleResetErrors();
      this.editableContact = {};
      this.showFormModal = false;
    },
    async handleProceed(data) {
      this.editable ? await this.handleUpdateContact(data) : await this.handleAddContact(data);
      this.handleClose();
    },
    async handleUpdateContact({ customer, password, ...rest }) {
      const { id: contactId } = this.editableContact;
      // skip password if not changed or empty
      const data =
        !password || password === DUMMY_PASSWORD
          ? { customer_id: customer.id, ...rest }
          : { customer_id: customer.id, password, ...rest };
      await this.$store.dispatch('admin/updateContact', { contactId, data });
    },
    async handleAddContact({ customer, ...rest }) {
      const data = { customer_id: customer.id, ...rest };
      await this.$store.dispatch('admin/addContact', { data });
    },
  },
  async mounted() {
    const { data } = await this.$http.get('admin/customers/find');
    this.customers = data;
  },
  tColumns: [],
  created() {
    this.$options.tColumns = [
      { column: 'subsidary', isSort: true, orderBy: 'id' },
      { column: 'first name', isSort: true, orderBy: 'first_name' },
      { column: 'last name', isSort: true, orderBy: 'last_name' },
      { column: 'email address', isSort: true, orderBy: 'email' },
      { column: 'phone #', isSort: true, orderBy: 'phone' },
      { column: 'business name', isSort: false, orderBy: '' },
      { column: 'invite sent at', isSort: true, orderBy: 'invite_sent_at' },
      { column: 'completed pretraining', isSort: true, orderBy: 'unlocked_trainings' },
      { column: 'actions', isSort: false },
    ];
  },
};
</script>
